import React from "react";

import { cn } from "~/components/ui/cn";
import { useFormatAddressQuery } from "~/hooks/use-format-address-query";
import { type Address } from "~/utils/address";

type AddressViewSize =
  | "large"
  | "medium"
  | "default"
  | "small"
  | "return-legacy";

export type AddressViewProps = {
  label: string;
  address: Address;
  locale: string;
  size?: AddressViewSize;
};

export const AddressView: React.FC<AddressViewProps> = ({
  label,
  address,
  locale,
  size = "large",
}: AddressViewProps) => {
  const { data: formattedAddress } = useFormatAddressQuery({
    address: {
      firstName: "",
      lastName: "",
      company: address.name,
      address1: address.street,
      address2: address.street2 ?? "",
      province: address.province,
      zip: address.bp,
      city: address.city,
      country: address.countryCode,
      phone: "",
    },
    locale: locale,
  });
  const mainRowClasses = (size: AddressViewSize) =>
    cn({
      "text-sm font-bold mb-2": size === "return-legacy",
      "text-xs text-gray-700": size === "small",
      "text-sm mb-1": size === "medium" || size === "default",
      "text-gray-700": size === "default",
      "text-base mb-2": size === "large",
      "font-semibold text-gray-700": size === "medium" || size === "large",
    });
  const secondaryRowClasses = (size: AddressViewSize) =>
    cn("text-gray-700 whitespace-pre", {
      "text-xs": size === "small" || size === "return-legacy",
      "text-sm": size === "default" || size === "medium" || size === "large",
    });

  return (
    <>
      <dt className="sr-only">{label}</dt>
      <dd>
        {formattedAddress?.map((addressFieldLabel, addressFieldIndex) =>
          address.name && addressFieldLabel === address.name ? (
            <div key={addressFieldIndex} className={mainRowClasses(size)}>
              {addressFieldLabel}
            </div>
          ) : (
            <div key={addressFieldIndex} className={secondaryRowClasses(size)}>
              {addressFieldLabel}
            </div>
          ),
        )}
      </dd>
    </>
  );
};
